import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { useSucursalesActivas } from "../lib/useSucursales";
import api, { apiErr } from "../lib/api";
import { ROLE_LABELS } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SearchBox, FilterSelect, matchQ } from "../components/SearchBox";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";
const ROLES = ["admin", "encargado", "encargado_comisionista", "repartidor", "comisionista", "punto", "cliente"];
const empty = { name: "", email: "", password: "", role: "encargado", sucursal: "ASU", whatsapp: "", comp_tipo: "salario", comision_pct: 0.1, comision_fija: 0 };

export default function Usuarios() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const SUCS = useSucursalesActivas().map((s) => s.code);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [fRol, setFRol] = useState("todos");
  const [fSuc, setFSuc] = useState("todas");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);
  const [delEnvios, setDelEnvios] = useState(false);

  const load = useCallback(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, sucursal: u.sucursal || "ASU", whatsapp: u.whatsapp || "", comp_tipo: u.comp_tipo || "salario", comision_pct: u.comision_pct || 0.1, comision_fija: u.comision_fija || 0 }); setShow(true); };

  const submit = async (e) => {
    e.preventDefault();
    const comp = { comp_tipo: form.comp_tipo, comision_pct: Number(form.comision_pct), comision_fija: Number(form.comision_fija) };
    try {
      if (editing) { await api.put(`/users/${editing.id}`, { name: form.name, role: form.role, sucursal: form.sucursal, whatsapp: form.whatsapp, ...comp, ...(form.password ? { password: form.password } : {}) }); toast.success("Usuario actualizado"); }
      else { await api.post("/users", { ...form, ...comp }); toast.success("Usuario creado"); }
      setShow(false); load();
    } catch (e2) { toast.error(apiErr(e2)); }
  };

  const doDelete = async () => {
    try {
      const { data } = await api.delete(`/users/${del.id}?borrar_envios=${delEnvios}`);
      toast.success(delEnvios ? `Usuario eliminado junto con ${data.envios_borrados || 0} envíos y ${data.retiros_borrados || 0} retiros` : "Usuario eliminado (sus envíos se conservaron)");
      setDel(null); setDelEnvios(false); load();
    }
    catch (e2) { toast.error(apiErr(e2)); }
  };

  const roleColor = { admin: "bg-[#0A1F44] text-white", cliente: "bg-slate-100 text-slate-700" };

  return (
    <>
      <Header title="Usuarios y Roles" subtitle="Control de accesos (RBAC · 7 roles)" onMenu={() => setMobileOpen(true)}
        action={<button onClick={openNew} data-testid="new-user-btn" className="flex items-center gap-2 text-sm font-semibold bg-[#0A1F44] hover:bg-[#132D5C] text-white rounded-lg px-3.5 py-2"><Plus className="h-4 w-4" /> Nuevo usuario</button>} />
      <main className="p-4 sm:p-6 lg:p-8">
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por nombre, email o WhatsApp…" testid="usuarios-search">
          <FilterSelect value={fRol} onChange={setFRol} testid="usuarios-filtro-rol" options={[["todos", "Todos los roles"], ...ROLES.map((r) => [r, ROLE_LABELS[r]])]} />
          <FilterSelect value={fSuc} onChange={setFSuc} testid="usuarios-filtro-suc" options={[["todas", "Todas las sucursales"], ...SUCS.map((s) => [s, s])]} />
        </SearchBox>
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[#64748B] text-xs uppercase">
                <tr><th className="text-left font-semibold px-4 py-3">Nombre</th><th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Email</th><th className="text-left font-semibold px-4 py-3">Rol</th><th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Sucursal</th><th className="text-right font-semibold px-4 py-3">Acciones</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.filter((u) => (fRol === "todos" || u.role === fRol) && (fSuc === "todas" || u.sucursal === fSuc)).filter(matchQ(q)).map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/60" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-semibold text-[#0A1F44]">{u.name}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-[#64748B]">{u.email}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${roleColor[u.role] || "bg-blue-50 text-blue-700"}`}>{ROLE_LABELS[u.role]}</span></td>
                    <td className="px-4 py-3 hidden md:table-cell text-[#64748B]">{u.sucursal || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                        {u.id !== user.id && <button onClick={() => setDel(u)} data-testid={`delete-user-${u.email}`} className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <input className={input} placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" />
            <input className={input} type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editing} data-testid="user-email-input" />
            <input className={input} type="password" placeholder={editing ? "Nueva contraseña (opcional)" : "Contraseña"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} data-testid="user-password-input" />
            <div className="grid grid-cols-2 gap-3">
              <select className={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, ...(e.target.value === "repartidor" ? { comp_tipo: "salario", comision_pct: 0.1, comision_fija: 0 } : {}) })} data-testid="user-role-select">
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
              <select className={input} value={form.sucursal} onChange={(e) => setForm({ ...form, sucursal: e.target.value })}>
                {SUCS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <input className={input} placeholder="WhatsApp (opcional)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            {form.role === "repartidor" && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1" data-testid="comp-config">
                <div className="text-xs font-semibold text-[#0A1F44] uppercase">Forma de pago</div>
                <div className="text-sm text-[#64748B]">Salario fijo (lo paga el admin). El repartidor a sueldo no cobra comisiones.</div>
              </div>
            )}
            {form.role === "encargado" && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid="comp-config">
                <div className="text-xs font-semibold text-[#0A1F44] uppercase">Forma de pago</div>
                <select className={input} value={form.comp_tipo} onChange={(e) => setForm({ ...form, comp_tipo: e.target.value })} data-testid="comp-tipo-select">
                  <option value="salario">Salario (lo paga el admin)</option>
                  <option value="comision">Comisión sobre flete</option>
                </select>
                {form.comp_tipo === "comision" && (
                  <select className={input} value={form.comision_pct} onChange={(e) => setForm({ ...form, comision_pct: e.target.value })} data-testid="comp-pct-select">
                    <option value={0.1}>10% del flete</option>
                    <option value={0.2}>20% del flete</option>
                    <option value={0.3}>30% del flete</option>
                  </select>
                )}
              </div>
            )}
            {["comisionista", "encargado_comisionista"].includes(form.role) && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid="comp-config">
                <div className="text-xs font-semibold text-[#0A1F44] uppercase">Comisión sobre flete</div>
                <select className={input} value={form.comision_pct} onChange={(e) => setForm({ ...form, comision_pct: e.target.value, comp_tipo: "comision", comision_fija: 0 })} data-testid="comp-pct-select">
                  <option value={0.1}>10% del flete</option>
                  <option value={0.2}>20% del flete</option>
                  <option value={0.3}>30% del flete</option>
                </select>
              </div>
            )}
            {form.role === "punto" && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid="comp-config">
                <div className="text-xs font-semibold text-[#0A1F44] uppercase">Comisión del Punto Pya'elino</div>
                <select className={input} value={Number(form.comision_fija) > 0 ? "fijo" : "porcentaje"}
                  onChange={(e) => setForm({ ...form, comp_tipo: "comision", comision_fija: e.target.value === "fijo" ? 5000 : 0 })} data-testid="punto-modo-select">
                  <option value="porcentaje">Porcentaje del flete</option>
                  <option value="fijo">Monto fijo por paquete</option>
                </select>
                {Number(form.comision_fija) > 0 ? (
                  <select className={input} value={form.comision_fija} onChange={(e) => setForm({ ...form, comision_fija: e.target.value })} data-testid="punto-fijo-select">
                    <option value={5000}>Gs. 5.000 por paquete</option>
                    <option value={10000}>Gs. 10.000 por paquete</option>
                  </select>
                ) : (
                  <select className={input} value={form.comision_pct} onChange={(e) => setForm({ ...form, comision_pct: e.target.value })} data-testid="comp-pct-select">
                    <option value={0.1}>10% del flete</option>
                    <option value={0.2}>20% del flete</option>
                    <option value={0.3}>30% del flete</option>
                  </select>
                )}
              </div>
            )}
            <button className="w-full rounded-lg bg-[#0A1F44] text-white font-semibold py-2.5 text-sm" data-testid="user-submit-btn">{editing ? "Guardar" : "Crear usuario"}</button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={() => { setDel(null); setDelEnvios(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará a <b>{del?.name}</b> ({del?.email}) junto con todos sus datos de registro y accesos. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="delete-envios-choice">
            <div className="text-sm font-semibold text-[#0A1F44] mb-2">¿Eliminar también los envíos realizados por este usuario?</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setDelEnvios(true)} data-testid="delete-envios-si"
                className={`rounded-lg py-2 text-sm font-semibold border transition-colors ${delEnvios ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
                Sí, borrar sus envíos
              </button>
              <button type="button" onClick={() => setDelEnvios(false)} data-testid="delete-envios-no"
                className={`rounded-lg py-2 text-sm font-semibold border transition-colors ${!delEnvios ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
                No, conservarlos
              </button>
            </div>
            {delEnvios && <p className="text-xs text-rose-600 mt-2">Se borrarán las boletas, retiros y solicitudes de reasignación creados por este usuario.</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-user">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
